import { IField, REQUIRED_FIELDS } from '../const/required-fields-const'
import { IHorseCompetitorDocument } from '../models/horse-competiton-paperwork/horse-competiton-paperwork-interface'
import { IProfileReqField } from '../pages/competitor/clinicAndOther/Tabs/components/ProfileFields'
import { IHorseData } from '../models/horse/horse.interface'
import { IUserPublicInterface } from '../models/users-public/users-public.interface'
import { IDateInterface } from '../models/interface.helper'

import { CountryList } from '../fakeData/countryList'
import { disciplineData } from '../fakeData/disciplineList'
import { horseBreedList } from '../fakeData/horseBreedList'
import { horseColorList } from '../fakeData/horseColorList'
import { horseHeightList } from '../fakeData/HorseHeightList'
import { zoneList } from '../fakeData/ZoneList'
import { amateurList } from '../fakeData/amateurList'
import { JudgingLicence } from '../fakeData/judgingLicence'
import { formatDate } from './helpers'

const HALF_YEAR = 1000 * 60 * 60 * 24 * 183
const ONE_YEAR = 1000 * 60 * 60 * 24 * 365

const getLabel = (arr: { label: string; value: string }[], value: string) => {
  const listItm = arr.find((itm) => itm.value === value)
  if (listItm?.label) return listItm.label
  return ''
}

const options: object = { day: '2-digit', month: 'short', year: '2-digit' }

export const getHorseProfileReqField = ({
  horse,
  field,
  horsePaperwork,
}: {
  horse: IHorseData | null
  field: IField
  horsePaperwork: IHorseCompetitorDocument | null
}) => {
  const profileReqField: IProfileReqField = {
    fieldKey: field.value,
    logo: field.img || '',
    title: field.title,
    type: field.type,
    isExpired: false,
    expiredDate: '',
    fieldUrl: '',
    fieldValue: '',
  }

  const value = horse?.[field.value as keyof IHorseData]

  if (field.type === 'paperwork') {
    let endDate: number = 0
    let date: string | Date = ''
    if (field.value === 'horseMeasurementCard') {
      const horseMeasurementCard = horsePaperwork?.horseMeasurementCards?.[0]
      date = horseMeasurementCard?.horseMeasurementDate || ''
      endDate = date ? new Date(date).getTime() : 0
      profileReqField.fieldUrl = horseMeasurementCard?.horseMeasurementCardsImage?.[0] || ''
    } else if (field.value === 'horseCoggins') {
      date = horsePaperwork?.cogginsInspectionDate || ''
      endDate = date ? new Date(date).getTime() : 0
      profileReqField.fieldUrl = horsePaperwork?.cogginsCertificate?.[0] || ''
    } else if (field.value === 'horseHealthCertificate') {
      date = horsePaperwork?.healthInspectionDate || ''
      endDate = date ? new Date(date).getTime() : 0
      profileReqField.fieldUrl = horsePaperwork?.healthCertificate?.[0] || ''
    } else if (field.value === 'horseRhinopneumonitis') {
      date = horsePaperwork?.rhinopneumonitisInspectionDate || ''
      endDate = date ? new Date(date).getTime() : 0
      profileReqField.fieldUrl = horsePaperwork?.rhinopneumonitisCertificate?.[0] || ''
    } else if (field.value === 'horseInfluenza') {
      date = horsePaperwork?.influenzaInspectionDate || ''
      endDate = date ? new Date(date).getTime() : 0
      profileReqField.fieldUrl = horsePaperwork?.influenzaCertificate?.[0] || ''
    }

    if (
      field.value === 'horseRhinopneumonitis' ||
      field.value === 'horseInfluenza' ||
      field.value === 'vaccinationCertificate'
    ) {
      profileReqField.isExpired = isExpiredVaccination(endDate)
    } else {
      profileReqField.isExpired = isExpiredPaperwork(endDate)
    }
    profileReqField.expiredDate = date ? new Date(endDate).toString() : ''
  } else if (field.type === 'date') {
    profileReqField.expiredDate = new Date(value as string).toString()
  } else if (field.type === 'tracking-number') {
    const cutValue = field.value.replace('Number', '')
    const valueExpiration = horse?.[(cutValue + 'Expiration') as keyof IHorseData] as Date
    profileReqField.fieldValue = value as string
    profileReqField.isExpired = isExpiredMembership(valueExpiration)
    profileReqField.expiredDate = valueExpiration ? new Date(valueExpiration).toString() : ''
  } else if (field.type === 'number') {
    const cutValue = field.value.replace('Number', '')
    const valueExpiration = horse?.[(cutValue + 'Expiration') as keyof IHorseData] as Date
    const valueImage = horse?.[(cutValue + 'Image') as keyof IHorseData] as string
    profileReqField.fieldValue = value as string
    profileReqField.isExpired = isExpiredMembership(valueExpiration)
    profileReqField.expiredDate = valueExpiration ? new Date(valueExpiration).toString() : ''
    profileReqField.fieldUrl = valueImage || ''
  } else {
    profileReqField.fieldValue = value as string
  }
  return profileReqField
}

export const getUserProfileReqField = ({
  field,
  user,
}: {
  field: IField
  user: IUserPublicInterface | null
}) => {
  const profileReqField: IProfileReqField = {
    fieldKey: field.value,
    logo: field.img || '',
    title: field.title,
    type: field.type,
    isExpired: false,
    expiredDate: '',
    fieldUrl: '',
    fieldValue: '',
  }

  const value = user?.[field.value as keyof IUserPublicInterface]

  if (field.type === 'number') {
    profileReqField.fieldValue = value?.documentNumber
    profileReqField.fieldUrl = value?.image || ''
    profileReqField.expiredDate = value?.endDate ? new Date(value.endDate).toString() : ''
    profileReqField.isExpired = isExpiredMembership(value?.endDate)
  } else if (field.type === 'date') {
    profileReqField.expiredDate = value ? new Date(value as string).toString() : ''
  } else {
    profileReqField.fieldValue = value as string
  }
  return profileReqField
}

export const getMembershipDate = (endDate: string | Date | null) => {
  if (!endDate) return ''
  // Check for lifetime membership date (12/31/2099)
  return endDate
    ? isLifetimeMembership(endDate)
      ? 'Lifetime'
      : `Exp. ${new Date(endDate).toLocaleDateString('en-GB', options).replace(',', '')}`
    : ''
}

export const getPaperworkDate = (endDate: string | Date | null) => {
  if (!endDate) return ''
  const date = new Date(endDate).getTime() + ONE_YEAR
  return `Exp. ${new Date(date).toLocaleDateString('en-GB', options).replace(',', '')}`
}
export const getVaccinationDate = (endDate: string | Date | null) => {
  if (!endDate) return ''
  const date = new Date(endDate).getTime() + HALF_YEAR
  return `Exp. ${new Date(date).toLocaleDateString('en-GB', options).replace(',', '')}`
}

const isLifetimeMembership = (endDate: any) => {
  return (
    endDate &&
    new Date(endDate.seconds ? endDate.seconds * 1000 : endDate).getFullYear() === 2099 &&
    new Date(endDate.seconds ? endDate.seconds * 1000 : endDate).getMonth() === 11 &&
    new Date(endDate.seconds ? endDate.seconds * 1000 : endDate).getDate() === 31
  )
}

export const isExpiredPaperwork = (expiration: any) => {
  if (!expiration) return false
  const today = new Date(Date.now()).getTime()
  const currentExpiration =
    new Date(expiration.seconds ? expiration.seconds * 1000 : expiration).getTime() + ONE_YEAR

  return currentExpiration < today
}

export const isExpiredVaccination = (expiration: any) => {
  if (!expiration) return false
  const today = new Date(Date.now()).getTime()
  const currentExpiration =
    new Date(expiration.seconds ? expiration.seconds * 1000 : expiration).getTime() + HALF_YEAR

  return currentExpiration < today
}

export const isExpiredMembership = (
  expiration?: Date | string | number | null | IDateInterface
) => {
  if (!expiration) return false
  const today = new Date(Date.now()).getTime()
  const exp = (expiration as IDateInterface).seconds
    ? (expiration as IDateInterface).seconds * 1000
    : (expiration as string)
  const currentExpiration = new Date(exp).getTime()
  return currentExpiration < today
}

export const isMissingRequired = (field: IProfileReqField) => {
  if (field.type === 'number') {
    return !field.fieldValue
  } else if (field.type === 'tracking-number') {
    return !field.fieldValue
  } else if (field.type === 'paperwork') {
    return !field.fieldUrl
  } else if (field.type === 'date') {
    return !field.expiredDate
  } else if (field.type === 'tel') {
    return typeof field.fieldValue === 'object'
  } else {
    return !field.fieldValue
  }
}

export const hasInvalidRequiredField = (field: IProfileReqField) => {
  if (field.type === 'number') {
    return !field.fieldValue || !field.expiredDate || field.isExpired
  } else if (field.type === 'tracking-number') {
    return !field.fieldValue || !field.expiredDate || field.isExpired
  } else if (field.type === 'paperwork') {
    return !field.fieldUrl || !field.expiredDate || field.isExpired
  } else if (field.type === 'date') {
    return !field.expiredDate || field.isExpired
  } else {
    return !field.fieldValue
  }
}

export const isInvalidRequired = (field: IProfileReqField) => {
  if (field.type === 'number') {
    return !field.fieldValue || !field.expiredDate
  } else if (field.type === 'tracking-number') {
    return !field.fieldValue || !field.expiredDate
  } else if (field.type === 'paperwork') {
    return !field.fieldUrl || !field.expiredDate
  } else if (field.type === 'date') {
    return !field.expiredDate
  } else {
    return !field.fieldValue
  }
}

export const reqFieldValue = (field: IProfileReqField) => {
  return field.type === 'number'
    ? getMembershipDate(field.expiredDate || null)
    : field.type === 'tracking-number'
      ? getMembershipDate(field.expiredDate || null)
      : field.type === 'date'
        ? formatDate(field.expiredDate || null)
        : field.fieldKey === 'horseRhinopneumonitis' ||
            field.fieldKey === 'horseInfluenza' ||
            field.fieldKey === 'vaccinationCertificate'
          ? getVaccinationDate(field.expiredDate || null)
          : field.type === 'paperwork'
            ? getPaperworkDate(field.expiredDate || null)
            : typeof field.fieldValue === 'object'
              ? ''
              : field.fieldValue
}

export const reqFieldLabel = (field: IProfileReqField) => {
  let label
  if (field.fieldKey === 'horseHeight') {
    label = getLabel(horseHeightList, field.fieldValue)
  } else if (field.fieldKey === 'horseZone') {
    label = getLabel(zoneList, field.fieldValue)
  } else if (field.fieldKey === 'horseColor') {
    label = getLabel(horseColorList, field.fieldValue)
  } else if (field.fieldKey === 'horseCountry') {
    label = getLabel(CountryList, field.fieldValue)
  } else if (field.fieldKey === 'horseDiscipline') {
    label = getLabel(disciplineData, field.fieldValue)
  } else if (field.fieldKey === 'userDiscipline') {
    label = getLabel(disciplineData, field.fieldValue)
  } else if (field.fieldKey === 'userAmateur') {
    label = getLabel(amateurList, field.fieldValue)
  } else if (field.fieldKey === 'userJudgingLicense') {
    label = getLabel(JudgingLicence, field.fieldValue)
  } else if (field.fieldKey === 'userNationality') {
    label = getLabel(CountryList, field.fieldValue)
  } else if (field.fieldKey === 'userZone') {
    label = getLabel(zoneList, field.fieldValue)
  } else if (field.fieldKey === 'horseBreed') {
    label = horseBreedList
      .filter((item) => field.fieldValue?.includes(item.value))
      .map((item) => item.label)
      .join(', ')
  } else if (field.type === 'paperwork') {
    if (
      field.fieldKey === 'horseRhinopneumonitis' ||
      field.fieldKey === 'horseInfluenza' ||
      field.fieldKey === 'vaccinationCertificate'
    ) {
      label = getVaccinationDate(field.expiredDate || null)
    } else {
      label = getPaperworkDate(field.expiredDate || null)
    }
  } else if (field.type === 'number') {
    label = `${field?.fieldValue}${field?.expiredDate && ` , ${getMembershipDate(field?.expiredDate)}`}`
  } else if (typeof field.fieldValue === 'object') {
    label = ''
  } else {
    label = field.fieldValue
  }

  return label
}

export const getUserRequiredFields = ({
  user,
  eventRequiredUserFields,
}: {
  user: IUserPublicInterface | null
  eventRequiredUserFields?: string[]
}): IProfileReqField[] => {
  const fields: IProfileReqField[] = []

  const requiredFieldsByValue = [
    ...REQUIRED_FIELDS.people.filter((itm) => eventRequiredUserFields?.includes(itm.value)),
    ...REQUIRED_FIELDS.peopleNumbers.filter((itm) => eventRequiredUserFields?.includes(itm.value)),
  ]

  requiredFieldsByValue.forEach((field) => {
    const profileReqField = getUserProfileReqField({ user, field })
    fields.push(profileReqField)
  })

  return fields
}

export const getHorseRequiredFields = ({
  horse,
  paperwork,
  eventRequiredHorseFields,
}: {
  horse: IHorseData | null
  paperwork: IHorseCompetitorDocument | null
  eventRequiredHorseFields?: string[]
}): IProfileReqField[] => {
  const fields: IProfileReqField[] = []
  const requiredFieldsByValue = [
    ...REQUIRED_FIELDS.horseMain.filter((itm) => eventRequiredHorseFields?.includes(itm.value)),
    ...REQUIRED_FIELDS.horseSecondary.filter((itm) =>
      eventRequiredHorseFields?.includes(itm.value)
    ),
    ...REQUIRED_FIELDS.horseNumbers.filter((itm) => eventRequiredHorseFields?.includes(itm.value)),
  ]

  requiredFieldsByValue.forEach((field) => {
    const profileReqField = getHorseProfileReqField({
      horse: horse,
      field,
      horsePaperwork: paperwork,
    })
    fields.push(profileReqField)
  })

  return fields
}
